/**
 * Unscheduled Tasks Panel
 *
 * Collapsible side panel showing tasks that have a dueDate on visible days
 * but no scheduledStart/End. Tasks can be dragged into the TimeGrid to schedule them.
 */

import { useState } from 'react';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import type { Task } from '@/types';

interface UnscheduledTasksPanelProps {
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
}

const priorityColors: Record<string, string> = {
  urgent: 'border-l-accent-rose',
  high: 'border-l-accent-amber',
  normal: 'border-l-accent-blue',
  low: 'border-l-accent-emerald',
};

export function UnscheduledTasksPanel({ tasks, onTaskClick }: UnscheduledTasksPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (tasks.length === 0) return null;

  return (
    <div
      className={clsx(
        'border-r border-border-primary bg-surface-secondary/30 flex flex-col shrink-0 transition-all duration-200',
        isCollapsed ? 'w-8' : 'w-56'
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-1.5 px-2 py-2.5 border-b border-border-primary">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-0.5 rounded hover:bg-surface-hover transition-colors"
        >
          {isCollapsed ? (
            <ChevronRight className="w-3.5 h-3.5 text-text-quaternary" />
          ) : (
            <ChevronLeft className="w-3.5 h-3.5 text-text-quaternary" />
          )}
        </button>
        {!isCollapsed && (
          <>
            <span className="text-[11px] font-semibold text-text-secondary truncate">
              Da pianificare
            </span>
            <span className="text-[10px] text-text-quaternary font-medium ml-auto">
              {tasks.length}
            </span>
          </>
        )}
      </div>

      {/* Task list */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
          {tasks.map((task) => (
            <div
              key={task.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/zenda-task', task.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onClick={() => onTaskClick?.(task)}
              className={clsx(
                'flex items-start gap-1.5 px-2 py-1.5 rounded-md border-l-2 cursor-grab active:cursor-grabbing',
                'bg-surface-primary hover:shadow-sm hover:bg-surface-hover/50 transition-all group',
                task.priority ? priorityColors[task.priority] || 'border-l-border-primary' : 'border-l-border-primary'
              )}
            >
              <GripVertical className="w-3 h-3 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-text-primary truncate">
                  {task.name}
                </p>
                {task.dueDate && (
                  <p className="text-[10px] text-text-quaternary mt-0.5">
                    {format(new Date(task.dueDate), 'd MMM', { locale: it })}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
